import { useState } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  Typography,
  Space,
  Alert,
  Tag,
  message,
} from 'antd';
import { MobileOutlined, SendOutlined, CopyOutlined } from '@ant-design/icons';
import { apiClient } from '../../lib/api-client';
import { t } from '../../i18n';

const { Title, Text, Paragraph } = Typography;

interface SendOtpResult {
  otpId: number;
  phone: string;
  code: string;
  expiresAt: string;
  smsSent: boolean;
}

export function OtpPage() {
  const [form] = Form.useForm<{ phone: string }>();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<SendOtpResult | null>(null);

  const onFinish = async (values: { phone: string }) => {
    let phone = values.phone.trim();
    if (!phone.startsWith('+')) {
      phone = '+' + phone.replace(/[^\d]/g, '');
    }
    if (!/^\+998\d{9}$/.test(phone)) {
      message.error(t('otp.invalidPhone'));
      return;
    }

    setLoading(true);
    setResult(null);
    try {
      const response = await apiClient.post<SendOtpResult>('/admin/otp/send', { phone });
      setResult(response.data);
      if (response.data.smsSent) {
        message.success(t('otp.success'));
      } else {
        message.warning(t('otp.smsFailedButCodeReady'));
      }
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } }; message?: string };
      message.error(error.response?.data?.message || error.message || t('otp.error'));
    } finally {
      setLoading(false);
    }
  };

  const copyCode = () => {
    if (result?.code) {
      navigator.clipboard.writeText(result.code);
      message.success(t('otp.copied'));
    }
  };

  return (
    <div style={{ padding: 24, maxWidth: 720 }}>
      <Title level={3}>{t('otp.title')}</Title>
      <Paragraph type="secondary">{t('otp.description')}</Paragraph>

      <Card>
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          initialValues={{ phone: '+998' }}
        >
          <Form.Item
            label={t('otp.phoneLabel')}
            name="phone"
            rules={[
              { required: true, message: t('otp.phoneRequired') },
              {
                pattern: /^\+998\d{9}$/,
                message: t('otp.invalidPhone'),
              },
            ]}
          >
            <Input
              size="large"
              prefix={<MobileOutlined />}
              placeholder="+998901234567"
              autoComplete="off"
            />
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              icon={<SendOutlined />}
              loading={loading}
              size="large"
              block
            >
              {t('otp.sendButton')}
            </Button>
          </Form.Item>
        </Form>
      </Card>

      {result && (
        <Card style={{ marginTop: 16 }}>
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <div>
              <Text type="secondary">{t('otp.phoneLabel')}: </Text>
              <Text strong>{result.phone}</Text>
            </div>

            <div>
              <Text type="secondary">{t('otp.codeLabel')}:</Text>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  marginTop: 4,
                }}
              >
                <Title
                  level={1}
                  style={{
                    margin: 0,
                    fontFamily: 'monospace',
                    letterSpacing: 8,
                  }}
                  copyable={{ text: result.code }}
                >
                  {result.code}
                </Title>
                <Button icon={<CopyOutlined />} onClick={copyCode}>
                  {t('otp.copy')}
                </Button>
              </div>
            </div>

            <div>
              <Text type="secondary">{t('otp.expiresAt')}: </Text>
              <Text>{new Date(result.expiresAt).toLocaleString()}</Text>
            </div>

            <div>
              <Text type="secondary">{t('otp.smsStatus')}: </Text>
              {result.smsSent ? (
                <Tag color="green">{t('otp.smsSent')}</Tag>
              ) : (
                <Tag color="orange">{t('otp.smsNotSent')}</Tag>
              )}
            </div>

            {!result.smsSent && (
              <Alert
                type="warning"
                showIcon
                message={t('otp.smsFailedTitle')}
                description={t('otp.smsFailedDesc')}
              />
            )}
          </Space>
        </Card>
      )}
    </div>
  );
}
